import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getSharedReport, fileUrl } from "../lib/api";
import { HeartPulse, Droplet, ReceiptText, Loader2, CalendarDays } from "lucide-react";
import logo from "../assets/logo.png";

const CAT_META = {
  bp: { label: "Blood Pressure reading", icon: HeartPulse },
  sugar: { label: "Sugar (blood glucose) test", icon: Droplet },
  medicine_bill: { label: "Medical bills", icon: ReceiptText },
};
const ORDER = ["bp", "sugar", "medicine_bill"];

export default function GuardianView() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    getSharedReport(token)
      .then(setReport)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading)
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center">
        <Loader2 className="animate-spin text-brand" size={28} />
      </div>
    );

  if (error || !report)
    return (
      <div className="min-h-screen bg-brand-cream flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="font-serif text-3xl text-brand-ink">Report not found</p>
        <p className="text-brand-ink/60">This visit link may be invalid or expired.</p>
        <Link to="/" className="text-brand font-medium hover:underline">Go to ElderCare24</Link>
      </div>
    );

  const grouped = ORDER.map((cat) => ({
    cat,
    photos: (report.photos || []).filter((p) => p.category === cat),
  })).filter((g) => g.photos.length > 0);

  return (
    <div className="min-h-screen bg-brand-cream">
      <header className="bg-brand-ink text-brand-cream">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center gap-3">
          <img src={logo} alt="ElderCare24" className="h-10 w-auto" />
          <div>
            <p className="font-serif text-2xl leading-none">
              Eldercare<span className="text-brand-gold">24</span>
            </p>
            <p className="text-xs text-brand-cream/60 mt-1">Home visit report</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10">
        <div className="rounded-3xl bg-white border border-border p-8 mb-8">
          <p className="text-xs font-semibold tracking-[0.2em] text-brand uppercase mb-3">
            Visit summary
          </p>
          <h1 className="font-serif text-4xl text-brand-ink mb-2">{report.parent_name}</h1>
          <p className="text-brand-ink/60 flex items-center gap-2 mb-6">
            <CalendarDays size={16} /> {report.visit_date || "Recent visit"} · for {report.guardian_name}
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            {report.bp_reading && (
              <div className="rounded-2xl bg-brand-sand p-5">
                <div className="flex items-center gap-2 text-brand-ink/60 text-sm mb-1">
                  <HeartPulse size={16} className="text-brand" /> Blood Pressure
                </div>
                <p className="font-serif text-3xl text-brand-ink">{report.bp_reading}</p>
              </div>
            )}
            {report.sugar_reading && (
              <div className="rounded-2xl bg-brand-sand p-5">
                <div className="flex items-center gap-2 text-brand-ink/60 text-sm mb-1">
                  <Droplet size={16} className="text-brand" /> Sugar test
                </div>
                <p className="font-serif text-3xl text-brand-ink">{report.sugar_reading}</p>
              </div>
            )}
          </div>

          {report.notes && (
            <div className="mt-5 rounded-2xl border border-border p-5">
              <p className="text-sm text-brand-ink/60 mb-1">Notes from our representative</p>
              <p className="text-brand-ink leading-relaxed">{report.notes}</p>
            </div>
          )}
        </div>

        {grouped.map(({ cat, photos }) => {
          const meta = CAT_META[cat];
          const Icon = meta.icon;
          return (
            <div key={cat} className="mb-8" data-testid={`guardian-group-${cat}`}>
              <h2 className="font-serif text-2xl text-brand-ink flex items-center gap-2 mb-4">
                <Icon size={22} className="text-brand" /> {meta.label}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {photos.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`guardian-photo-${p.id}`}
                    onClick={() => setLightbox(fileUrl(p.url))}
                    className="group relative aspect-square rounded-2xl overflow-hidden border border-border bg-white"
                  >
                    <img
                      src={fileUrl(p.url)}
                      alt={meta.label}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </button>
                ))}
              </div>
            </div>
          );
        })}

        {grouped.length === 0 && (
          <p className="text-center text-brand-ink/50 py-10">
            Photos for this visit will appear here shortly.
          </p>
        )}

        <p className="text-center text-xs text-brand-ink/40 mt-10">
          Shared securely by ElderCare24, Jaipur.
        </p>
      </main>

      {lightbox && (
        <div
          data-testid="guardian-lightbox"
          className="fixed inset-0 z-50 bg-brand-ink/90 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <img src={lightbox} alt="Visit photo" className="max-h-[90vh] max-w-full rounded-xl" />
        </div>
      )}
    </div>
  );
}
